
/**
 * Reconstruye placeholders ${...} aunque estén rotos entre múltiples <w:t>, <w:r>, etc.
 * Idea:
 *  - Cuando encontramos un '$' fuera de etiquetas, miramos hacia delante ignorando tags y espacios.
 *  - Si lo siguiente "real" es '{', consideramos que empieza un placeholder.
 *  - Vamos consumiendo texto (ignorando tags y espacios) hasta llegar al primer '}'.
 *  - Todo lo leído entre '${' y '}' se almacena en un único string, por ejemplo:
 *      ${v:solicitud.empresa.vatCode}
 *  - Ese string se inserta tal cual en el XML en la posición donde cerraba el placeholder.
 *
 *  No nos atamos a <w:t>, ni a <w:r>, solo respetamos todas las etiquetas tal cual.
 */

export function reconstruirPlaceholders(xml) {
  let result = ''
  let dentroTag = false
  let dentroPlaceholder = false
  let saltandoHastaLlave = false
  let placeholder = ''
  const n = xml.length
  let i = 0

  function esInicioPlaceholder(pos) {
    // Mira hacia delante desde pos+1 ignorando etiquetas y espacios,
    // y devuelve true si el primer carácter de texto real es '{'
    let j = pos + 1
    let dentroTagLocal = false

    while (j < n) {
      const c = xml[j]

      if (c === '<') {
        dentroTagLocal = true
        j++
        continue
      }
      if (c === '>' && dentroTagLocal) {
        dentroTagLocal = false
        j++
        continue
      }
      if (dentroTagLocal) {
        j++
        continue
      }

      // Estamos fuera de etiquetas: ignoramos espacios / saltos de línea
      if (/\s/.test(c)) {
        j++
        continue
      }

      // Primer carácter "real" encontrado
      return c === '{'
    }

    return false
  }

  while (i < n) {
    const c = xml[i]

    // ===========================
    // 1️⃣ Gestión de etiquetas XML
    // ===========================
    if (c === '<') {
      dentroTag = true
      result += c
      i++
      continue
    }

    if (c === '>' && dentroTag) {
      dentroTag = false
      result += c
      i++
      continue
    }

    if (dentroTag) {
      // Dentro de cualquier <...> copiamos tal cual
      result += c
      i++
      continue
    }

    // ===========================
    // 2️⃣ Texto FUERA de etiquetas
    // ===========================

    // Aún no estamos dentro de un placeholder
    if (!dentroPlaceholder) {
      if (c === '$' && esInicioPlaceholder(i)) {
        // Detectamos inicio de placeholder: ${...}
        dentroPlaceholder = true
        saltandoHastaLlave = true
        placeholder = '${' // metemos ya '${', la '{' real la saltaremos luego
        i++
        continue
      } else {
        // Texto normal
        result += c
        i++
        continue
      }
    }

    // ===========================
    // 3️⃣ Ya estamos dentro de un placeholder
    // ===========================

    if (saltandoHastaLlave) {
      // Estamos entre el '$' y la '{' real, ignorando espacios y etiquetas
      if (/\s/.test(c)) {
        // Espacios de formato en el XML: los conservamos fuera del placeholder
        result += c
        i++
        continue
      }

      if (c === '{') {
        // Hemos encontrado la '{' que esperábamos → empezamos a capturar contenido real
        saltandoHastaLlave = false
        i++
        continue
      }

      // Si llegamos aquí es que el lookahead se equivocó (caso muy raro),
      // degradamos el '$' a texto normal.
      result += '$'
      dentroPlaceholder = false
      saltandoHastaLlave = false
      placeholder = ''
      // No avanzamos i aquí para volver a procesar este carácter como texto normal
      continue
    }

    // Después de la '{' capturamos únicamente el contenido "útil" del placeholder
    // Ignorando espacios de formato y saltos de línea
    if (/\s/.test(c)) {
      result += c
      i++
      continue
    }

    placeholder += c

    if (c === '}') {
      // Cerramos placeholder
      result += placeholder
      dentroPlaceholder = false
      placeholder = ''
    }

    i++
  }

  // Si por algún motivo nos quedamos con placeholder abierto, lo añadimos tal cual
  if (dentroPlaceholder && placeholder) {
    result += placeholder
  }

  return result
}
